import { ThemeEvents, CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const MAX_ITEMS = 3;
const THRESHOLD_DEFAULT = 5000;

/** @type {any[] | null} */
let catalogCache = null;
/** @type {Promise<any[]> | null} */
let catalogPromise = null;

class MuskleCartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {boolean} */
  #loading = false;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#refresh();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #onCartUpdate = () => {
    this.#refresh();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const variantId = Number(button.dataset.variantId || 0);
    if (!variantId) return;
    event.preventDefault();
    this.#addVariant(variantId, button);
  };

  async #refresh() {
    try {
      const cart = await fetch(this.#url('cart.js')).then((response) => response.json());
      if (!cart || cart.item_count < 1) {
        this.hidden = true;
        return;
      }

      const threshold = Number(this.dataset.thresholdCents || THRESHOLD_DEFAULT);
      const total = Number(cart.total_price || 0);
      const remaining = Math.max(0, threshold - total);
      const unlocked = remaining <= 0;
      const cartProductIds = new Set((cart.items || []).map((item) => String(item.product_id)));
      const seedIds = (cart.items || []).map((item) => String(item.product_id)).filter(Boolean);

      this.dataset.totalCents = String(total);
      this.classList.toggle('is-complete', unlocked);
      const heading = this.querySelector('[data-upsell-heading]');
      if (heading) {
        heading.textContent = unlocked ? this.dataset.headingUnlocked || '' : this.dataset.headingComplete || '';
      }

      const products = await this.#loadRecommendations(seedIds, cartProductIds, remaining);
      this.#render(products);
    } catch {
      if (!this.querySelector('[data-upsell-list] li')) this.hidden = true;
    }
  }

  /**
   * @param {string[]} seedIds
   * @param {Set<string>} cartProductIds
   * @param {number} remaining
   */
  async #loadRecommendations(seedIds, cartProductIds, remaining) {
    /** @type {Map<string, any>} */
    const byId = new Map();

    const collectList = (products) => {
      for (const product of products) this.#collect(byId, product, cartProductIds);
    };

    collectList(await this.#loadCatalog());

    if (byId.size < MAX_ITEMS && seedIds[0]) {
      collectList(await this.#fetchRecommendations(seedIds[0], 'related'));
    }

    const list = [...byId.values()];
    list.sort((a, b) => this.#score(a.price, remaining) - this.#score(b.price, remaining));
    return list.slice(0, MAX_ITEMS);
  }

  /**
   * @param {Map<string, any>} byId
   * @param {any} product
   * @param {Set<string>} cartProductIds
   */
  #collect(byId, product, cartProductIds) {
    const normalized = this.#normalize(product);
    if (!normalized) return;
    if (cartProductIds.has(normalized.productId)) return;
    if (byId.has(normalized.productId)) return;
    byId.set(normalized.productId, normalized);
  }

  async #loadCatalog() {
    if (catalogCache) return catalogCache;
    if (!catalogPromise) {
      catalogPromise = this.#buildCatalog();
    }
    try {
      catalogCache = await catalogPromise;
      return catalogCache;
    } catch {
      catalogPromise = null;
      return [];
    }
  }

  async #buildCatalog() {
    const handles = ['amino-acids', 'vitaminen-mineralen', 'creatine', 'all'];
    /** @type {any[]} */
    const merged = [];
    for (const handle of handles) {
      const products = await this.#fetchCollection(handle);
      merged.push(...products);
      if (merged.length >= 12) break;
    }
    return merged;
  }

  /**
   * @param {string} productId
   * @param {string} intent
   */
  async #fetchRecommendations(productId, intent) {
    const query = intent
      ? `recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=${encodeURIComponent(intent)}`
      : `recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8`;
    return this.#fetchProducts(query);
  }

  /**
   * @param {string} handle
   */
  async #fetchCollection(handle) {
    return this.#fetchProducts(`collections/${encodeURIComponent(handle)}/products.json?limit=50`);
  }

  /**
   * @param {string} path
   */
  async #fetchProducts(path) {
    try {
      const data = await this.#fetchJson(path);
      return Array.isArray(data?.products) ? data.products : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} path
   */
  async #fetchJson(path) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(this.#url(path), { signal: controller.signal });
      if (!response.ok) return {};
      return response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * @param {any} product
   */
  #normalize(product) {
    if (!product) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item && item.id && item.available !== false);
    if (!variant) return null;

    const firstImage = product.images?.[0];
    const image =
      (typeof product.featured_image === 'string' && product.featured_image) ||
      product.featured_image?.src ||
      variant.featured_image?.src ||
      (typeof firstImage === 'string' && firstImage) ||
      firstImage?.src ||
      null;
    if (!image) return null;

    const price = toCents(variant.price ?? product.price);
    if (!price) return null;

    return {
      productId: String(product.id),
      variantId: String(variant.id),
      title: String(product.title || '').trim(),
      image: String(image).replace(/^\/\//, 'https://'),
      price,
    };
  }

  /**
   * @param {number} price
   * @param {number} remaining
   */
  #score(price, remaining) {
    if (remaining <= 0) return Math.abs(price - 1990);
    const bandBonus = price >= 1000 && price <= 2500 ? 0 : 400;
    return Math.abs(price - remaining) + bandBonus;
  }

  /**
   * @param {Array<{productId: string, variantId: string, title: string, image: string, price: number}>} products
   */
  #render(products) {
    const list = this.querySelector('[data-upsell-list]');
    if (!(list instanceof HTMLElement)) return;

    if (!products.length) {
      if (!list.querySelector('li')) this.hidden = true;
      return;
    }

    const addLabel = this.dataset.addLabel || 'Add';
    list.replaceChildren(
      ...products.map((product) => {
        const item = document.createElement('li');
        item.className = 'muskle-cart-upsell__item';
        item.innerHTML = `
          <img class="muskle-cart-upsell__image" alt="" width="48" height="48" loading="lazy">
          <div class="muskle-cart-upsell__copy">
            <p class="muskle-cart-upsell__title"></p>
            <span class="muskle-cart-upsell__price"></span>
          </div>
          <button type="button" class="muskle-cart-upsell__add" data-upsell-add></button>
        `;
        const image = item.querySelector('.muskle-cart-upsell__image');
        const title = item.querySelector('.muskle-cart-upsell__title');
        const price = item.querySelector('.muskle-cart-upsell__price');
        const button = item.querySelector('[data-upsell-add]');
        if (image instanceof HTMLImageElement) image.src = product.image;
        if (title) title.textContent = product.title;
        if (price) price.textContent = this.#format(product.price);
        if (button instanceof HTMLButtonElement) {
          button.textContent = addLabel;
          button.dataset.variantId = product.variantId;
          button.dataset.productId = product.productId;
        }
        return item;
      })
    );
    this.hidden = false;
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   */
  async #addVariant(variantId, button) {
    if (this.#loading) return;
    this.#loading = true;
    button.disabled = true;

    try {
      const addResponse = await fetch(this.#url('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const added = await addResponse.json();
      if (!addResponse.ok) throw new Error(added?.description || added?.message || 'add failed');

      const cart = await fetch(this.#url('cart.js')).then((response) => response.json());
      document.dispatchEvent(
        new CartAddEvent(cart, this.getAttribute('data-hydration-key') || 'muskle-cart-upsell', {
          source: 'muskle-cart-upsell',
          productId: button.dataset.productId,
          variantId: String(variantId),
          itemCount: cart.item_count,
          didError: false,
        })
      );
    } catch {
      button.disabled = false;
    } finally {
      this.#loading = false;
    }
  }

  /**
   * @param {string} path
   */
  #url(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path.replace(/^\//, '')}`;
  }

  /**
   * @param {number} cents
   */
  #format(cents) {
    return formatMoney(Math.max(0, cents), this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }
}

/**
 * Shopify Ajax collection prices are "24.90"; recommendation prices are often cents.
 * @param {unknown} value
 */
function toCents(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (!Number.isInteger(value) && value < 1000) return Math.round(value * 100);
    return Math.round(value);
  }
  const raw = String(value).trim().replace(',', '.');
  if (!raw) return 0;
  if (raw.includes('.')) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

if (!customElements.get('muskle-cart-upsell')) {
  customElements.define('muskle-cart-upsell', MuskleCartUpsell);
}
